import { apiRequest } from './authService';

/**
 * Get all gold lots (per-purity) for a customer.
 * @param {string|number} customerId
 * @returns {Promise<{success: boolean, data?: Array<{id, customer_id, purity, amount_grams, created_at, updated_at}>, message?: string}>}
 */
export async function getCustomerGoldLots(customerId) {
  try {
    const response = await apiRequest('gold/customer', {
      method: 'POST',
      body: JSON.stringify({ customer_id: customerId })
    });
    if (response.success && response.data) {
      return { success: true, data: response.data, message: response.response };
    }
    return { success: false, data: [], message: response.response || 'Failed to fetch lots' };
  } catch (e) {
    console.error('getCustomerGoldLots error:', e);
    return { success: false, data: [], message: 'Network error. Please try again.' };
  }
}

/**
 * Deposit gold at a given purity. Merges into existing lot of same purity, or creates a new one.
 * @param {string|number} customerId
 * @param {number} purity 0..100
 * @param {number} amountGrams positive
 * @param {string} [description]
 * @param {number} [createdAt] Unix seconds (optional, defaults to server time)
 */
export async function depositGold(customerId, purity, amountGrams, description = '', createdAt = null) {
  try {
    const body = {
      customer_id: customerId,
      purity: purity,
      amount_grams: Math.abs(amountGrams),
      description: description,
    };
    if (createdAt !== null && createdAt !== undefined && createdAt !== '') body.created_at = createdAt;

    const response = await apiRequest('gold/deposit', {
      method: 'POST',
      body: JSON.stringify(body)
    });
    if (response.success) {
      return { success: true, data: response.data, message: response.response };
    }
    return { success: false, message: response.response || 'Failed to deposit gold' };
  } catch (e) {
    console.error('depositGold error:', e);
    return { success: false, message: 'Network error. Please try again.' };
  }
}

/**
 * Withdraw gold from a SPECIFIC lot. Amount must be <= lot's current grams.
 * @param {string|number} customerId
 * @param {string|number} lotId
 * @param {number} amountGrams positive
 * @param {string} [description]
 * @param {number} [createdAt] Unix seconds (optional)
 */
export async function withdrawGold(customerId, lotId, amountGrams, description = '', createdAt = null) {
  try {
    const body = {
      customer_id: customerId,
      lot_id: lotId,
      amount_grams: Math.abs(amountGrams),
      description: description,
    };
    if (createdAt !== null && createdAt !== undefined && createdAt !== '') body.created_at = createdAt;

    const response = await apiRequest('gold/withdrawal', {
      method: 'POST',
      body: JSON.stringify(body)
    });
    if (response.success) {
      return { success: true, data: response.data, message: response.response };
    }
    return { success: false, message: response.response || 'Failed to withdraw gold' };
  } catch (e) {
    console.error('withdrawGold error:', e);
    return { success: false, message: 'Network error. Please try again.' };
  }
}

/**
 * Get gold transactions for a customer (newest first).
 */
export async function getCustomerGoldTransactions(customerId, purity = null, limit = 100) {
  try {
    const body = { customer_id: customerId, limit };
    if (purity !== null && purity !== undefined && purity !== '') body.purity = purity;
    const response = await apiRequest('gold-transactions/customer', {
      method: 'POST',
      body: JSON.stringify(body)
    });
    if (response.success && response.data) {
      return { success: true, data: response.data, message: response.response };
    }
    return { success: false, data: [], message: response.response || 'Failed to fetch transactions' };
  } catch (e) {
    console.error('getCustomerGoldTransactions error:', e);
    return { success: false, data: [], message: 'Network error. Please try again.' };
  }
}

export async function searchCustomerGoldTransactions(customerId, query, limit = 100) {
  try {
    const response = await apiRequest('gold-transactions/search', {
      method: 'POST',
      body: JSON.stringify({ customer_id: customerId, query, limit })
    });
    if (response.success && response.data) {
      return { success: true, data: response.data, message: response.response };
    }
    return { success: false, data: [], message: response.response || 'Failed to search' };
  } catch (e) {
    console.error('searchCustomerGoldTransactions error:', e);
    return { success: false, data: [], message: 'Network error. Please try again.' };
  }
}

export async function deleteGoldTransaction(transactionId) {
  try {
    const response = await apiRequest('gold-transactions/delete', {
      method: 'POST',
      body: JSON.stringify({ transaction_id: transactionId })
    });
    if (response.success) {
      return { success: true, data: response.data, message: response.response };
    }
    return { success: false, message: response.response || 'Failed to delete' };
  } catch (e) {
    console.error('deleteGoldTransaction error:', e);
    return { success: false, message: 'Network error. Please try again.' };
  }
}

export async function editGoldTransaction(transactionId, { amount_grams, description = '' }) {
  try {
    const response = await apiRequest('gold-transactions/edit', {
      method: 'POST',
      body: JSON.stringify({
        transaction_id: transactionId,
        amount_grams,
        description
      })
    });
    if (response.success) {
      return { success: true, data: response.data, message: response.response };
    }
    return { success: false, message: response.response || 'Failed to edit transaction' };
  } catch (e) {
    console.error('editGoldTransaction error:', e);
    return { success: false, message: 'Network error. Please try again.' };
  }
}

export async function getGoldTransactionsHistory(filters = {}) {
  try {
    const body = {};
    if (filters.date_from !== undefined && filters.date_from !== null && filters.date_from !== '') body.date_from = filters.date_from;
    if (filters.date_to !== undefined && filters.date_to !== null && filters.date_to !== '') body.date_to = filters.date_to;
    if (filters.customer_id) body.customer_id = filters.customer_id;
    if (filters.purity !== undefined && filters.purity !== null && filters.purity !== '') body.purity = filters.purity;
    if (filters.type) body.type = filters.type;
    if (filters.limit) body.limit = filters.limit;

    const response = await apiRequest('gold-transactions/history', {
      method: 'POST',
      body: JSON.stringify(body)
    });
    if (response.success && response.data) {
      return { success: true, data: response.data, message: response.response };
    }
    return { success: false, message: response.response || 'Failed to fetch history' };
  } catch (e) {
    console.error('getGoldTransactionsHistory error:', e);
    return { success: false, message: 'Network error. Please try again.' };
  }
}

/**
 * Aggregate totals across all customers.
 * @returns {Promise<{success, data?: {total_grams: number, by_purity: Array<{purity, grams}>}, message?}>}
 */
export async function getAllClientsGoldBalance() {
  try {
    const response = await apiRequest('gold/all-clients-balance', { method: 'POST' });
    if (response.success && response.data) {
      return { success: true, data: response.data, message: response.response };
    }
    return { success: false, data: { total_grams: 0, by_purity: [] }, message: response.response || 'Failed' };
  } catch (e) {
    console.error('getAllClientsGoldBalance error:', e);
    return { success: false, data: { total_grams: 0, by_purity: [] }, message: 'Network error.' };
  }
}

/**
 * Get full gold export bundle (customer + lots + all transactions) for PDF.
 * @param {string|number} customerId
 * @returns {Promise<{success: boolean, data?: {customer, lots, transactions}, message?: string}>}
 */
export async function getGoldExport(customerId) {
  try {
    const response = await apiRequest('gold-transactions/export', {
      method: 'POST',
      body: JSON.stringify({ customer_id: customerId })
    });
    if (response.success && response.data) {
      return { success: true, data: response.data, message: response.response };
    }
    return { success: false, message: response.response || 'Failed to fetch export data' };
  } catch (e) {
    console.error('getGoldExport error:', e);
    return { success: false, message: 'Network error. Please try again.' };
  }
}

/**
 * Compute total grams (sum of lot amounts) for a customer.
 */
export async function getCustomerGoldTotalGrams(customerId) {
  const r = await getCustomerGoldLots(customerId);
  if (!r.success || !Array.isArray(r.data)) return 0;
  return r.data.reduce((sum, lot) => sum + parseFloat(lot.amount_grams || 0), 0);
}
